import { Dispatch } from "redux";

import {
  getRootUserId,
  Currency,
  getCurrencySymbol,
} from "./core/generalUtils";
import { APP_CONFIG } from "./storage/appConfig";

import { ApiTagType, RTKApi } from "@/app/app.types";
import { APP_LOCAL_KEYS } from "@/app/constants/constants";
import { customToast } from "@/components/alerts/CustomToast";
import { SocketData } from "@/interfaces/globals";

const workspaceData = JSON.parse(
  localStorage.getItem(APP_LOCAL_KEYS.WORKSPACE_DATA) || "null",
);

export const getWorkspace = (): string => {
  const id = localStorage.getItem(APP_LOCAL_KEYS.WORKSPACE_ID);
  if (!id) {
    return workspaceData?.id;
  }

  return id;
};

export const tableUpdateToast = (
  data: SocketData,
  msg: string,
  id?: string,
): void => {
  if (data?.userId !== getRootUserId() && data?.action) {
    customToast.success(
      msg,
      {
        position: "bottom-left",
      },
      id,
    );
  }
};

interface HandleSocketUpdateConfig<TApi extends RTKApi> {
  socketData: SocketData;
  dispatch: Dispatch;
  api: TApi;
  tags: ApiTagType[];
  moduleName: string;
}

const localMutations = new Set<string>();
let mutationChannel: BroadcastChannel | null = null;

/**
 * Get or create the BroadcastChannel for mutation notifications.
 * NOTE: The channel is used to notify other tabs to INVALIDATE their cache,
 * not to skip updates. Each tab only skips updates for mutations IT made.
 * @returns {BroadcastChannel | null} The BroadcastChannel instance or null if not supported
 */
const getMutationChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }

  if (!mutationChannel) {
    mutationChannel = new BroadcastChannel(APP_CONFIG.MUTATION_CHANNEL_NAME);
  }

  return mutationChannel;
};

getMutationChannel();

/**
 * Notify other tabs that a mutation occurred.
 * This tab tracks the mutation locally to skip its own socket update.
 * Other tabs will receive the socket event and invalidate normally.
 * @param {string} moduleName - The module/feature name (e.g., "Customer")
 * @param {string} action - The action type (e.g., "create", "update", "delete")
 * @returns {string} The mutation ID that was stored locally
 * @example
 * // After a successful mutation
 * const mutationId = notifyMutationToOtherTabs("Customer", "update");
 */
export const notifyMutationToOtherTabs = (
  moduleName: string,
  action: string,
): string => {
  const mutationId = `${moduleName}-${action}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  // Only track in THIS tab's local set - other tabs should invalidate
  localMutations.add(mutationId);

  // Broadcast is kept for potential future use (e.g., optimistic UI sync)
  const channel = getMutationChannel();
  channel?.postMessage({ mutationId });

  setTimeout(() => {
    localMutations.delete(mutationId);
  }, APP_CONFIG.MUTATION_EXPIRY_MS);

  return mutationId;
};

/**
 * Check if THIS tab recently performed a mutation for this module.
 * Only checks local mutations, not broadcasts from other tabs.
 * @param {string} moduleName - The module/feature name
 * @returns {boolean} True if THIS tab recently mutated this module
 */
export const hasLocalMutation = (moduleName: string): boolean => {
  for (const mutationId of localMutations) {
    if (mutationId.startsWith(`${moduleName}-`)) {
      return true;
    }
  }

  return false;
};

/**
 * Handle socket updates for cache invalidation.
 * Supports multi-tab scenarios correctly:
 * - Only the tab that made the mutation skips the update (cache already updated)
 * - Other tabs of the same user invalidate silently (no toast)
 * - Different users invalidate and see a toast notification
 *
 * This ensures:
 * - Tab that made the change: No duplicate update (cache already updated by mutation)
 * - Other tabs of same user: Silently invalidate cache (user initiated the change)
 * - Different users: Invalidate cache and show toast notification
 * @template TApi - RTK Query API type
 * @param {object} config - Configuration object
 * @param {SocketData} config.socketData - Socket event data
 * @param {Dispatch} config.dispatch - Redux dispatch function
 * @param {TApi} config.api - RTK Query API instance (e.g., clientApi)
 * @param {ApiTagType[]} config.tags - Tags to invalidate (use API_TAGS constants)
 * @param {string} config.moduleName - Module name for toast message
 * @returns {void}
 * @example
 * import { API_TAGS } from "@/app/constants/apiTags.constants";
 *
 * socket?.on(SOCKET_KEYS.CLIENTS_CUSTOMER, (data: SocketData) => {
 *   handleSocketUpdate({
 *     socketData: data,
 *     dispatch,
 *     api: clientApi,
 *     tags: [API_TAGS.CLIENTS.CUSTOMER],
 *     moduleName: MODULE_CONSTANTS.CLIENTS.CUSTOMER,
 *   });
 * });
 */
export const handleSocketUpdate = <TApi extends RTKApi>({
  socketData,
  dispatch,
  api,
  tags,
  moduleName,
}: HandleSocketUpdateConfig<TApi>): void => {
  if (!socketData?.action) {
    return;
  }

  const currentUserId = getRootUserId();
  const isFromCurrentUser = socketData?.userId === currentUserId;

  // For current user's actions, check if THIS tab made the mutation
  if (isFromCurrentUser) {
    // Check if THIS tab made the mutation (has it in localMutations)
    if (hasLocalMutation(moduleName)) {
      // This tab made the mutation, cache already updated - skip
      return;
    }

    // Another tab of the same user made the mutation
    // Silently invalidate cache without notification
    dispatch(api.util.invalidateTags(tags));

    return;
  }

  // Different user made the change
  // Invalidate cache and show notification
  dispatch(api.util.invalidateTags(tags));
  customToast.success(`${moduleName} table updated!`, {
    position: "bottom-left",
  });
};

export const formatFinancialValue = (
  value: number,
  fixedPoint: number = 2,
  currencyType?: Currency,
): string => {
  const _actualDecimalPlaces =
    value % 1 === 0
      ? 0
      : Math.min(
          fixedPoint,
          value.toString().split(".")[1]?.replace(/0+$/, "").length || 0,
        );

  const formattedValue = (value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: _actualDecimalPlaces,
    useGrouping: true,
  });

  if (currencyType) {
    const symbol = getCurrencySymbol(currencyType);

    return `${symbol}${formattedValue}`;
  }

  return formattedValue;
};
